import { cn } from '@/lib/utils';
import type { ControlPostureRow } from '@/api/types/compliance.types';

/**
 * Shared Control-detail presentation, used by BOTH the split-pane and the full-page route.
 * One implementation so the two views cannot drift — a control read one way must not describe
 * itself differently when read the other.
 *
 * ⚠ Only sections with real canonical data are rendered. Questions, assignments, evidence,
 * policies and activity are absent because their models do not exist on the canonical spine
 * yet, not because they were styled out. The card stack is the shape they slot into.
 */

// ── Vocabulary ────────────────────────────────────────────────────────────────────────

export const STATUS_LABEL: Record<string, string> = {
  implemented: 'Implemented',
  partially_implemented: 'Partially implemented',
  not_implemented: 'Not implemented',
  not_applicable: 'Not applicable',
  unknown: 'Unknown',
};
export const EFFECTIVENESS_LABEL: Record<string, string> = {
  effective: 'Effective',
  partially_effective: 'Partially effective',
  ineffective: 'Ineffective',
  not_applicable: 'Not applicable',
  unknown: 'Unknown',
};
export const NOT_ASSESSED = 'Not assessed yet';
export const UNASSESSED = 'text-text-faint italic';

const EFFECTIVE_VALUES = new Set(['implemented', 'effective']);
const PARTIAL_VALUES = new Set(['partially_implemented', 'partially_effective']);

export function toneFor(v: string | null): string {
  if (v === null) return UNASSESSED;
  if (EFFECTIVE_VALUES.has(v)) return 'text-cert-valid-fg';
  if (PARTIAL_VALUES.has(v)) return 'text-cert-warn-fg';
  if (v === 'not_implemented' || v === 'ineffective') return 'text-cert-expired-fg';
  return 'text-text-secondary';
}
function swatchFor(v: string | null): string {
  if (v === null) return 'bg-text-faint/50';
  if (EFFECTIVE_VALUES.has(v)) return 'bg-cert-valid-fg';
  if (PARTIAL_VALUES.has(v)) return 'bg-cert-warn-fg';
  if (v === 'not_implemented' || v === 'ineffective') return 'bg-cert-expired-fg';
  return 'bg-text-secondary';
}
export const labelFor = (v: string | null, m: Record<string, string>) =>
  (v === null ? NOT_ASSESSED : (m[v] ?? v));

/**
 * ⚠ The denominator counts only axes actually ASSESSED, with any remainder stated separately,
 * so "1 of 2 axes effective · 1 not assessed" can never be read as one axis having failed.
 */
export function axisSummary(row: ControlPostureRow): { text: string; tone: string; trailing?: string } {
  if (row.applicability === 'not_applicable') return { text: 'Not applicable', tone: 'text-text-secondary' };
  const axes = [row.implementationStatus, row.designEffectiveness, row.operatingEffectiveness];
  const assessed = axes.filter((a) => a !== null && a !== 'unknown');
  if (assessed.length === 0) return { text: NOT_ASSESSED, tone: UNASSESSED };
  const effective = assessed.filter((a) => EFFECTIVE_VALUES.has(a as string)).length;
  const unassessed = axes.length - assessed.length;
  const tone = effective === assessed.length ? 'text-cert-valid-fg'
    : effective === 0 ? 'text-cert-expired-fg' : 'text-cert-warn-fg';
  return {
    text: `${effective} of ${assessed.length} axes effective`,
    tone,
    trailing: unassessed > 0 ? `${unassessed} not assessed` : undefined,
  };
}

// ── Shared primitives ─────────────────────────────────────────────────────────────────

/** Panel border is a step stronger than an internal divider — regions read as regions. */
export const PANEL = 'rounded-[10px] border border-cert-neutral-border bg-bg-surface';
export const EYEBROW = 'text-[10px] font-bold uppercase tracking-[0.12em] text-text-secondary';
export const SECTION_LABEL = cn(EYEBROW, 'block');

export function MetaChip({ children, title }: { children: React.ReactNode; title?: string }) {
  return (
    <span
      title={title}
      className="shrink-0 rounded-[4px] border border-cert-neutral-border bg-bg-surface-2/60 px-[6px] py-[1.5px] text-[10.5px] text-text-muted whitespace-nowrap"
    >
      {children}
    </span>
  );
}

export function DetailCard({
  label, right, children,
}: { label: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="rounded-[8px] border border-cert-neutral-border bg-bg-surface-2/45">
      <div className="flex items-center justify-between gap-[10px] px-[12px] pt-[10px]">
        <h3 className={EYEBROW}>{label}</h3>
        {right}
      </div>
      <div className="px-[12px] pb-[11px] pt-[8px]">{children}</div>
    </section>
  );
}

// ── Sections ──────────────────────────────────────────────────────────────────────────

export function AssessmentCard({ row }: { row: ControlPostureRow }) {
  return (
    <DetailCard label="Assessment">
      <div className="flex flex-col gap-[5px]">
        {([
          ['Implementation', row.implementationStatus, STATUS_LABEL],
          ['Design effectiveness', row.designEffectiveness, EFFECTIVENESS_LABEL],
          ['Operating effectiveness', row.operatingEffectiveness, EFFECTIVENESS_LABEL],
        ] as const).map(([label, value, map]) => (
          <div key={label} className="flex items-center justify-between gap-[10px]">
            <span className="text-[11.5px] text-text-secondary">{label}</span>
            <span className="flex items-center gap-[7px]">
              <span className={cn('h-[7px] w-[7px] rounded-[2px]', swatchFor(value))} aria-hidden />
              <span className={cn('text-[11.5px] font-semibold', toneFor(value))}>{labelFor(value, map)}</span>
            </span>
          </div>
        ))}
        <div className="flex items-center justify-between gap-[10px]">
          <span className="text-[11.5px] text-text-secondary">Maturity</span>
          {/* An ordinal, never a percentage and never floored to zero. */}
          <span className={cn('text-[11.5px] font-semibold', row.maturityLevel === null ? UNASSESSED : 'text-text-primary')}>
            {row.maturityLevel === null ? NOT_ASSESSED : `Level ${row.maturityLevel} of 5`}
          </span>
        </div>
        {row.applicability === 'not_applicable' && (
          <div className="flex items-center justify-between gap-[10px] border-t border-border-subtle pt-[6px]">
            <span className="text-[11.5px] text-text-secondary">Applicability</span>
            <span className="text-[11.5px] font-semibold text-text-secondary">Not applicable</span>
          </div>
        )}
      </div>
    </DetailCard>
  );
}

export function FunctionsCard({ row }: { row: ControlPostureRow }) {
  return (
    <DetailCard label="Functions">
      {row.functions.length ? (
        <div className="flex flex-wrap gap-[5px]">
          {row.functions.map((f) => <MetaChip key={f}>{f}</MetaChip>)}
        </div>
      ) : (
        <span className={cn('text-[11.5px]', UNASSESSED)}>None recorded</span>
      )}
    </DetailCard>
  );
}

export function RequirementsCard({ row }: { row: ControlPostureRow }) {
  const withText = row.requirements.filter((q) => q.title);
  return (
    <DetailCard
      label="Mapped framework requirements"
      right={row.requirements.length > 0
        ? <span className="text-[10px] font-semibold tabular-nums text-text-secondary">{row.requirements.length}</span>
        : undefined}
    >
      {row.requirements.length === 0 ? (
        <span className={cn('text-[11.5px]', UNASSESSED)}>No enabled framework currently requires this control</span>
      ) : (
        <div className="flex flex-col gap-[8px]">
          <p className="text-[10.5px] leading-[1.5] text-text-faint">
            Worked once, this control counts for all {row.requirements.length} of these requirements.
          </p>

          {/* ⚠ Rendered, NOT linked. The framework-requirement destination does not exist yet,
              and a dead link is worse than plain text. The two-way map
              (control → requirement → mapped controls) activates when that page is built. */}
          <div className="flex flex-wrap gap-[6px]">
            {row.requirements.map((q) => (
              <span
                key={`${q.frameworkKey}-${q.refId}`}
                className="flex shrink-0 items-stretch overflow-hidden rounded-[5px] border border-cert-neutral-border"
              >
                <span className="bg-bg-surface-2 px-[7px] py-[2.5px] text-[10px] font-semibold text-text-muted whitespace-nowrap">
                  {q.frameworkName}
                </span>
                <span className="px-[7px] py-[2.5px] font-mono text-[10px] font-semibold text-text-secondary whitespace-nowrap">
                  {q.refId}
                </span>
              </span>
            ))}
          </div>

          {withText.map((q) => (
            <div key={q.refId} className="rounded-[6px] border border-cert-info-border bg-cert-info-bg/50 px-[10px] py-[9px]">
              <span className="font-mono text-[10px] font-bold text-cert-info-fg">{q.refId}</span>
              <p className="mt-[4px] text-[11.5px] leading-[1.5] text-text-primary">{q.title}</p>
              {q.description && q.description !== q.title && (
                <p className="mt-[4px] text-[10.5px] leading-[1.5] text-text-muted">{q.description}</p>
              )}
            </div>
          ))}
          {withText.length > 0 ? (
            // Attribution travels WITH the text — a licensing condition, not a page footer.
            <p className="text-[9.5px] leading-[1.5] text-text-faint">
              Requirement text from the NIST Cybersecurity Framework 2.0, a work of the U.S. National
              Institute of Standards and Technology. Text for the other frameworks is not shown in this release.
            </p>
          ) : (
            <p className="text-[9.5px] leading-[1.5] text-text-faint">
              Requirement text is not shown for these frameworks in this release.
            </p>
          )}
        </div>
      )}
    </DetailCard>
  );
}

/** Identity block — reference, axis summary, title, description. */
export function ControlHeading({ row, large }: { row: ControlPostureRow; large?: boolean }) {
  const summary = axisSummary(row);
  return (
    <div className="flex flex-col gap-[7px]">
      <div className="flex flex-wrap items-center gap-[10px]">
        <span className="font-mono text-[11px] font-bold text-text-secondary">{row.publicId ?? '—'}</span>
        <span className={cn('text-[10.5px] font-semibold', summary.tone)}>{summary.text}</span>
        {summary.trailing && <span className="text-[10.5px] text-text-faint">· {summary.trailing}</span>}
      </div>
      <h2 className={cn('font-bold leading-tight text-text-primary', large ? 'text-[20px]' : 'text-[15px]')}>
        {row.title ?? 'Untitled control'}
      </h2>
      {row.description && (
        <p className={cn('leading-[1.6] text-text-muted', large ? 'max-w-[92ch] text-[12.5px]' : 'text-[11.5px]')}>
          {row.description}
        </p>
      )}
    </div>
  );
}
